import React from 'react';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import * as Yup from 'yup';
import useReportQueryParams from '../../hooks/use-report-query-params';
import { ReportDateRange, ReportEntityType, ReportProduct } from '../../types';
import { Formik, useFormikContext, Form } from 'formik';
import { FilterFormValues } from './types';
import WdtFilter from './WdtFilter';
import KeyboardingFilter from './KeyboardingFilter';
import PrmFilter from './PrmFilter';
import cx from 'clsx';

const useStyles = makeStyles(theme => {
  return {
    container: {
      [theme.breakpoints.up('lg')]: {
        position: 'sticky'
      },
      zIndex: 100,
      top: 0,
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(3),
      boxShadow: theme.shadows[2],
      background: 'white'
    },
    submitButtonContainer: {
      marginLeft: 'auto'
    },
    noPrint: {
      '@media print': { display: 'none' }
    }
  };
});

const validationSchema = Yup.object({
  entityType: Yup.string().required(),
  productFamilyCode: Yup.string().required(),
  entityId: Yup.number().required(),
  displayBy: Yup.string()
    .nullable()
    .when(
      ['productFamilyCode', 'entityType'],
      (productFamilyCode: ReportProduct, entityType: ReportEntityType) => {
        if (productFamilyCode === ReportProduct.WDT && entityType === ReportEntityType.CLASSROOM) {
          return Yup.string().required();
        }
      }
    ),
  dateRange: Yup.string()
    .oneOf(Object.values(ReportDateRange))
    .required()
});

const ReportFilter = () => {
  const [reportQuery, setQuery] = useReportQueryParams();
  const classes = useStyles();

  return (
    <Formik
      enableReinitialize
      initialValues={reportQuery}
      validateOnBlur
      validateOnChange
      validateOnMount={false}
      validationSchema={validationSchema}
      onSubmit={values => {
        setQuery({ ...values });
      }}
    >
      <Form translate="false" className={cx(classes.container, classes.noPrint)}>
        <Grid container spacing={2} alignItems="flex-end">
          <ProductFilter />
          <Grid item className={classes.submitButtonContainer}>
            <Button variant="contained" color="primary" disableElevation type="submit">
              <Typography noWrap>Generate Report</Typography>
            </Button>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  );
};

const PRODUCT_REPORT_FILTER: { [key in ReportProduct]?: any } = {
  [ReportProduct.KEY]: KeyboardingFilter,
  [ReportProduct.PRM]: PrmFilter,
  [ReportProduct.WDT]: WdtFilter
};

const ProductFilter = () => {
  const { values } = useFormikContext<FilterFormValues>();
  const { productFamilyCode } = values;
  const Filter = PRODUCT_REPORT_FILTER[productFamilyCode];

  // Ensure mount/unmount behavior with key usage
  return <Filter key={productFamilyCode} />;
};

export default ReportFilter;
